import React, { useCallback, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { bubblePath } from '../utils/bubblePath';
interface DockNoteProps {
  text: string;
  /** Newest note: carries the iMessage tail at its bottom-right, pointing down at Pixel */
  tail: boolean;
  reduce: boolean;
}

/** iMessage received-bubble grey */
const FILL = '#E9E9EB';
/** iMessage corner radius relative to the text size (single lines stay a full pill) */
const RADIUS_EM = 1.27;
/** One soft curve for entering, shifting and leaving, so the whole stack moves as one */
const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * One of Pixel's notes in the corner chat — a grey iMessage bubble. The newest note's body and tail
 * are one continuous shape (the same outline as the hero bubbles, mirrored so the tail curls out of
 * the bottom-right toward Pixel). Older notes are plain pills.
 */
export const DockNote = React.forwardRef<HTMLDivElement, DockNoteProps>(function DockNote({
  text,
  tail,
  reduce
}, ref) {
  const [box, setBox] = useState({
    w: 0,
    h: 0,
    r: 0
  });
  const observer = useRef<ResizeObserver | null>(null);
  const measureRef = useCallback((el: HTMLParagraphElement | null) => {
    observer.current?.disconnect();
    if (!el) return;
    const measure = () => {
      const fontSize = parseFloat(getComputedStyle(el).fontSize) || 15;
      const h = el.offsetHeight;
      setBox({
        w: el.offsetWidth,
        h,
        r: Math.min(fontSize * RADIUS_EM, h / 2)
      });
    };
    observer.current = new ResizeObserver(measure);
    observer.current.observe(el);
    measure();
  }, []);

  // Shape drawn as one path once measured; until then (first frame) a plain pill background
  const shaped = tail && box.w > 0;
  return <motion.div ref={ref} layout={reduce ? false : 'position'} className="relative w-max max-w-[min(300px,calc(100vw-140px))] origin-bottom-right" initial={reduce ? false : {
    opacity: 0,
    y: 10,
    scale: 0.94,
    filter: 'blur(4px)'
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)'
  }} exit={reduce ? {
    opacity: 0,
    transition: {
      duration: 0
    }
  } : {
    opacity: 0,
    y: -8,
    scale: 0.97,
    filter: 'blur(4px)',
    transition: {
      duration: 0.28,
      ease: EASE
    }
  }} transition={reduce ? {
    duration: 0
  } : {
    duration: 0.42,
    ease: EASE,
    layout: {
      duration: 0.42,
      ease: EASE
    }
  }} data-id="element-59" data-mp="1wimpubnn72di">
      {shaped && <svg aria-hidden="true" width={box.w} height={box.h} viewBox={`0 0 ${box.w} ${box.h}`}
    // Mirrored: the hero outline's bottom-left tail becomes a bottom-right tail
    className="pointer-events-none absolute left-0 top-0 -scale-x-100 overflow-visible" data-id="element-60" data-mp="2k7pug0jojsb2">
          <path d={bubblePath(box.w, box.h, box.r, true)} fill={FILL} data-id="element-61" data-mp="2yv17y7zf9mdt" />
        </svg>}
      <p ref={measureRef}
    // Always left-aligned; multi-line notes keep iMessage corners instead of a stretched capsule
    className="relative m-0 px-[0.9em] py-[0.42em] text-left text-[clamp(0.92rem,1.035vw,1.15rem)] font-medium leading-[1.29] tracking-[-0.015em] text-black [text-wrap:pretty]" style={{
      backgroundColor: shaped ? 'transparent' : FILL,
      borderRadius: box.r || 9999
    }} data-id="element-62" data-mp="33w6zi2hcj3lr">
        <span className="sr-only" data-id="element-63" data-mp="n990hh2qj1n5">Pixel: </span>
        {text}
      </p>
    </motion.div>;
});